import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';
import { fetchAllIpoRecords } from '../../api/ipoCatalogPages';
import { mapIpoDetailResponse } from '../../utils/ipoDataMapper';
import { isSameCompany, matchKey } from '../../utils/smeIpos';

// The catalog list is shared by every issuer lookup for this long
const CATALOG_MAX_AGE_MS = 10 * 60 * 1000;

const isFresh = (entry, maxAgeMs) =>
  Boolean(maxAgeMs && entry?.status === 'succeeded' && entry.fetchedAt && Date.now() - entry.fetchedAt < maxAgeMs);

/**
 * Category-wise bid book of one issue (single company subscription page)
 * GET /api/market/subscription/{symbol}?force=false
 *
 * options.symbol: NSE symbol, upper case
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchIssueSubscription = createAsyncThunk(
  'issueSubscription/fetchIssueSubscription',
  async ({ symbol }, { rejectWithValue }) => {
    try {
      const response = await ipoService.getSubscriptionBySymbol(symbol);
      if (response?.success === false) {
        return rejectWithValue(response.error || 'Failed to fetch subscription details');
      }
      const data = response?.data || {};
      return { rows: Array.isArray(data.data) ? data.data : [], refreshedAt: data.refreshed_at || null };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch subscription details');
    }
  },
  {
    condition: ({ symbol, maxAgeMs = 0 }, { getState }) => {
      const entry = getState().issueSubscription.bySymbol[symbol];
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (entry?.status === 'loading') return false;
      return !isFresh(entry, maxAgeMs);
    },
  }
);

// The catalog can list a company twice: prefer the extracted DRHP, then the newest
const leadFirst = (a, b) =>
  Number(Boolean(b.extractor_version)) - Number(Boolean(a.extractor_version)) ||
  (b.updated_at || '').localeCompare(a.updated_at || '');

/**
 * Catalog record, extracted DRHP details and broker reviews for an issuer,
 * found by company name in GET /api/core/ipos, then
 * GET /api/core/ipos/{id} and GET /api/core/ipos/{id}/broker-reviews.
 * A company that isn't in the catalog resolves with recordId null.
 */
export const fetchIssuerProfile = createAsyncThunk(
  'issueSubscription/fetchIssuerProfile',
  async ({ company }, { getState, rejectWithValue }) => {
    try {
      const { catalog } = getState().issueSubscription;
      let records = catalog.records;
      let catalogFetchedAt = catalog.fetchedAt;
      if (!catalogFetchedAt || Date.now() - catalogFetchedAt >= CATALOG_MAX_AGE_MS) {
        ({ records } = await fetchAllIpoRecords());
        catalogFetchedAt = Date.now();
      }

      const [record] = records.filter((entry) => entry?.company_name && isSameCompany(entry.company_name, company)).sort(leadFirst);
      if (!record) return { records, catalogFetchedAt, recordId: null, detail: null, reviews: null };

      const [detailResult, reviewsResult] = await Promise.allSettled([
        ipoService.getIpoById(record.id),
        ipoService.getBrokerReviews(record.id),
      ]);
      return {
        records,
        catalogFetchedAt,
        recordId: record.id,
        detail: detailResult.status === 'fulfilled' ? mapIpoDetailResponse(detailResult.value) : null,
        reviews: reviewsResult.status === 'fulfilled' ? reviewsResult.value?.data || null : null,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch issuer details');
    }
  },
  {
    condition: ({ company, maxAgeMs = 0 }, { getState }) => {
      if (!company) return false;
      const entry = getState().issueSubscription.profiles[matchKey(company)];
      if (entry?.status === 'loading') return false;
      return !isFresh(entry, maxAgeMs);
    },
  }
);

const initialState = {
  // Keyed by NSE symbol: { rows, refreshedAt, fetchedAt, status, error }
  bySymbol: {},
  // Keyed by matchKey(company): { recordId, detail, reviews, fetchedAt, status, error }
  profiles: {},
  // Cached GET /api/core/ipos list shared by issuer lookups
  catalog: { records: [], fetchedAt: null },
};

export const issueSubscriptionSlice = createSlice({
  name: 'issueSubscription',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchIssueSubscription.pending, (state, action) => {
        const { symbol } = action.meta.arg;
        state.bySymbol[symbol] = { rows: [], refreshedAt: null, fetchedAt: null, ...state.bySymbol[symbol], status: 'loading', error: null };
      })
      .addCase(fetchIssueSubscription.fulfilled, (state, action) => {
        const { symbol } = action.meta.arg;
        state.bySymbol[symbol] = { ...action.payload, fetchedAt: Date.now(), status: 'succeeded', error: null };
      })
      .addCase(fetchIssueSubscription.rejected, (state, action) => {
        // Keep the previous book so a failed refresh doesn't blank the page
        const { symbol } = action.meta.arg;
        state.bySymbol[symbol] = {
          rows: [],
          refreshedAt: null,
          fetchedAt: null,
          ...state.bySymbol[symbol],
          status: 'failed',
          error: action.payload || 'Failed to load subscription details',
        };
      })
      .addCase(fetchIssuerProfile.pending, (state, action) => {
        const key = matchKey(action.meta.arg.company);
        state.profiles[key] = { recordId: null, detail: null, reviews: null, fetchedAt: null, ...state.profiles[key], status: 'loading', error: null };
      })
      .addCase(fetchIssuerProfile.fulfilled, (state, action) => {
        const { records, catalogFetchedAt, ...profile } = action.payload;
        state.catalog = { records, fetchedAt: catalogFetchedAt };
        state.profiles[matchKey(action.meta.arg.company)] = { ...profile, fetchedAt: Date.now(), status: 'succeeded', error: null };
      })
      .addCase(fetchIssuerProfile.rejected, (state, action) => {
        const key = matchKey(action.meta.arg.company);
        state.profiles[key] = {
          recordId: null,
          detail: null,
          reviews: null,
          fetchedAt: null,
          ...state.profiles[key],
          status: 'failed',
          error: action.payload || 'Failed to load issuer details',
        };
      });
  },
});

export default issueSubscriptionSlice.reducer;
